import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Crown, Check, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { isSubActive } from "@/utils/subscription";

export default function SubscriptionPage() {
  const { user, api } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState("annual");  // дефолт annual (акцент на річному)
  const active = isSubActive(user);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const res = await api().post("/billing/checkout", { plan });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (e) {
      setLoading(false);
    }
  };

  const [portalErr, setPortalErr] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);
  const openPortal = async () => {
    setPortalErr("");
    setPortalLoading(true);
    try {
      const res = await api().post("/billing/portal");
      window.location.href = res.data.url;
    } catch (e) {
      console.error(e);
      setPortalErr(t("subPortalError"));
      setPortalLoading(false);
    }
  };

  // Оптимістичний user на маунті йде без поля subscription (AuthContext), а isSubActive
  // на невідомому стані повертає true — без цієї ознаки сторінка встигала намалювати
  // "Ваша підписка активна" тому, у кого підписки немає. Правка локальна для екрана:
  // isSubActive і subscription.js не чіпаємо.
  const subUnknown = !!user && user.subscription === undefined;
  const usedTrial = !!(user?.subscription?.trial_end);
  const priceLabel = plan === "annual" ? t("subPriceYear") : t("subPriceMonth");
  const ctaLabel = usedTrial ? t("subCtaSubscribe") : t("subCta");
  // Дата першого списання для того, хто ще не витратив тріал: сьогодні + 7 днів
  // (STRIPE_TRIAL_DAYS на бекенді). Тернар локалі — той самий, що в активній гілці
  // нижче; спільний хелпер свідомо не робимо, це окрема задача.
  const trialLoc = lang === "uk" ? "uk-UA" : lang === "ru" ? "ru-RU" : "en-US";
  const firstChargeDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    .toLocaleDateString(trialLoc, { day: "2-digit", month: "2-digit", year: "numeric" });

  const benefits = [
    t("subBenefit1"), t("subBenefit2"), t("subBenefit3"),
    t("subBenefit4"), t("subBenefit5"), t("subBenefit6"), t("subBenefit7"),
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6] pb-24">
      <div className="max-w-md mx-auto px-4 pt-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-black/60 mb-4">
          <ArrowLeft className="w-5 h-5" /> {t("back")}
        </button>

        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mb-3">
            <Crown className="w-8 h-8 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold">{t("subTitle")}</h1>
          <p className="text-black/60 mt-1">{t("subSubtitle")}</p>
        </div>

        {subUnknown ? (
          /* Стан ще не відомий: нейтральний скелетон — ні тексту, ні галки, ні корони,
             щоб екран нічого не стверджував про підписку, поки не долетів /auth/me. */
          <div className="bg-white rounded-2xl p-5 border border-black/5" aria-hidden="true">
            <div className="w-8 h-8 rounded-full bg-[#E8E5E0] mx-auto mb-3 animate-pulse" />
            <div className="h-4 w-44 bg-[#E8E5E0] rounded-full mx-auto animate-pulse" />
            <div className="h-3 w-32 bg-[#EBE9E4] rounded-full mx-auto mt-2.5 animate-pulse" />
          </div>
        ) : active ? (
          <div className="bg-white rounded-2xl p-5 border border-black/5 text-center">
            <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="font-medium">{t("subActiveMsg")}</p>
            {(() => {
              const sub = user?.subscription || {};
              const loc = lang === "uk" ? "uk-UA" : lang === "ru" ? "ru-RU" : "en-US";
              const fmt = (d) => (d ? new Date(d).toLocaleDateString(loc, { day: "2-digit", month: "2-digit", year: "numeric" }) : "");
              let statusLine = null;
              if (sub.cancel_at_period_end) {
                statusLine = <p className="text-sm mt-1 text-[#BC4749]">{t("subCanceledUntil")} {fmt(sub.cancel_at || sub.current_period_end)}</p>;
              } else if (sub.trial_end && new Date(sub.trial_end) > new Date()) {
                statusLine = <p className="text-sm mt-1 text-black/60">{t("subTrialUntil")} {fmt(sub.trial_end)}</p>;
              } else if (sub.current_period_end) {
                statusLine = <p className="text-sm mt-1 text-black/60">{t("subNextBilling")} {fmt(sub.current_period_end)}</p>;
              }
              return (
                <>
                  {statusLine}
                  {sub.stripe_customer_id && (
                    <button
                      onClick={openPortal}
                      disabled={portalLoading}
                      className="w-full bg-[#7B9E7F] text-white font-semibold py-4 rounded-2xl mt-4 disabled:opacity-60"
                    >
                      {t("subManage")}
                    </button>
                  )}
                  {portalErr && <div className="text-[#BC4749] text-sm mt-2">{portalErr}</div>}
                </>
              );
            })()}
          </div>
        ) : (
          <>
            {usedTrial && (
              /* Пояснює, ЧОМУ юзер тут: тріал був і скінчився. Свіжому акаунту
                 (usedTrial === false) не показуємо — йому пропонується тріал. */
              <div className="bg-amber-100 rounded-2xl p-5 border border-black/5 mb-4 text-center">
                <p className="font-medium">{t("subTrialEndedTitle")}</p>
                <p className="text-sm text-black/60 mt-1">{t("subTrialEndedBody")}</p>
              </div>
            )}
            <div className="bg-white rounded-2xl p-5 border border-black/5 mb-4">
              <div className="flex gap-2 mb-4">
                {/* ANNUAL — перший, акцентований */}
                <button
                  onClick={() => setPlan("annual")}
                  className={`flex-1 rounded-2xl border p-3 text-left relative ${plan === "annual" ? "border-[#7B9E7F] bg-[#7B9E7F]/5" : "border-black/10"}`}
                >
                  <div className="absolute -top-2 right-3 bg-[#7B9E7F] text-white text-[10px] px-2 py-0.5 rounded-full">{t("subBestValue")}</div>
                  <div className="font-semibold">{t("subPlanYear")}</div>
                  <div className="text-lg font-bold">{t("subPriceYear")}</div>
                  <div className="text-xs text-black/50">{t("subSaveNote")}</div>
                </button>
                {/* MONTHLY */}
                <button
                  onClick={() => setPlan("monthly")}
                  className={`flex-1 rounded-2xl border p-3 text-left ${plan === "monthly" ? "border-[#7B9E7F] bg-[#7B9E7F]/5" : "border-black/10"}`}
                >
                  <div className="font-semibold">{t("subPlanMonth")}</div>
                  <div className="text-lg font-bold">{t("subPriceMonth")}</div>
                </button>
              </div>
              <p className="text-center text-black/60 text-sm mb-4">
                {usedTrial ? priceLabel : `${t("subTrialPrefix")} ${priceLabel}`}
              </p>
              <ul className="space-y-2">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                    <span className="text-sm">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="w-full bg-[#7B9E7F] text-white font-semibold py-4 rounded-2xl disabled:opacity-60"
            >
              {loading ? t("subLoading") : ctaLabel}
            </button>
            {/* Розкриття умов. subFinePrint знято з екрана: він обіцяв нагадування
                перед списанням, а розсилки нагадувань немає. Ключ у translations.js
                лишається на майбутнє. */}
            <div className="text-xs text-black/40 text-center mt-3 space-y-1">
              <p>{t("subAutoRenew")}</p>
              <p>
                {usedTrial
                  ? t("subFirstChargeNow")
                  : t("subFirstChargeTrial").replace("{date}", firstChargeDate)}
              </p>
              <p>
                <Link to="/terms" className="underline">{t("subTermsLink")}</Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
